import React from "react";

// Hero concept: one stage that becomes four pictures from the same shirt.
// Scenes are drawn in SVG as stand-ins. Give a scene `src` (image) or `video`
// (muted loop) to replace the drawing with a real render.

const SHIRT = "#6f7447";
const SCENE_SECONDS = 3.2;

const Figure = ({ id, skin, hair, hairStyle, bottom, bottomType, shoe = "#1c1a19", sway = false, transform }) => {
    const skinShade = "rgba(60,25,10,.28)";
    return (
        <g transform={transform}><g className={sway ? "hp2-sway" : undefined}>
            <defs>
                <linearGradient id={`${id}-shirt`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#fff" stopOpacity=".22" />
                    <stop offset=".45" stopColor="#fff" stopOpacity="0" />
                    <stop offset="1" stopColor="#000" stopOpacity=".28" />
                </linearGradient>
                <linearGradient id={`${id}-bottom`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#fff" stopOpacity=".12" />
                    <stop offset="1" stopColor="#000" stopOpacity=".3" />
                </linearGradient>
                <radialGradient id={`${id}-skin`} cx=".4" cy=".35" r=".8">
                    <stop offset="0" stopColor="#fff" stopOpacity=".18" />
                    <stop offset="1" stopColor="#000" stopOpacity=".22" />
                </radialGradient>
            </defs>

            <ellipse cx="100" cy="347" rx="56" ry="8" fill="rgba(0,0,0,.28)" />

            {hairStyle === "long" && <path d="M79 46 C76 14 124 14 121 46 L127 112 C112 118 88 118 73 112 Z" fill={hair} />}

            {/* legs / skirt */}
            {bottomType === "skirt" ? (
                <>
                    <path d="M74 186 L126 186 L142 296 C120 302 80 302 58 296 Z" fill={bottom} />
                    <path d="M74 186 L126 186 L142 296 C120 302 80 302 58 296 Z" fill={`url(#${id}-bottom)`} />
                    <rect x="86" y="296" width="9" height="42" fill={skin} />
                    <rect x="105" y="296" width="9" height="42" fill={skin} />
                </>
            ) : (
                <>
                    <path d="M75 186 L125 186 L130 334 L106 334 L100 236 L94 334 L70 334 Z" fill={bottom} />
                    <path d="M75 186 L125 186 L130 334 L106 334 L100 236 L94 334 L70 334 Z" fill={`url(#${id}-bottom)`} />
                </>
            )}
            <ellipse cx="82" cy="340" rx="14" ry="6" fill={shoe} />
            <ellipse cx="118" cy="340" rx="14" ry="6" fill={shoe} />

            {/* arms (forearms) */}
            <path d="M41 150 L53 154 L50 200 L39 198 Z" fill={skin} />
            <path d="M159 150 L147 154 L150 200 L161 198 Z" fill={skin} />
            <circle cx="44" cy="204" r="6" fill={skin} />
            <circle cx="156" cy="204" r="6" fill={skin} />

            {/* shirt */}
            <path d="M66 94 C80 85 120 85 134 94 L131 122 C129 150 127 172 125 196 L75 196 C73 172 71 150 69 122 Z" fill={SHIRT} />
            <path d="M66 94 L47 99 L40 152 L53 156 L65 122 Z" fill={SHIRT} />
            <path d="M134 94 L153 99 L160 152 L147 156 L135 122 Z" fill={SHIRT} />
            <path d="M66 94 C80 85 120 85 134 94 L131 122 C129 150 127 172 125 196 L75 196 C73 172 71 150 69 122 Z" fill={`url(#${id}-shirt)`} />
            <path d="M66 94 L47 99 L40 152 L53 156 L65 122 Z" fill={`url(#${id}-shirt)`} />
            <path d="M134 94 L153 99 L160 152 L147 156 L135 122 Z" fill={`url(#${id}-shirt)`} />
            <path d="M82 130 C90 140 90 160 84 176 M118 124 C112 140 112 160 118 180 M96 196 C98 190 102 190 104 196" stroke="rgba(0,0,0,.2)" strokeWidth="1.6" fill="none" strokeLinecap="round" />
            <path d="M100 104 L100 196" stroke="rgba(0,0,0,.18)" strokeWidth="1.2" />
            {[118, 140, 162].map((y) => <circle key={y} cx="100" cy={y} r="1.6" fill="rgba(255,255,255,.55)" />)}
            <path d="M89 88 L100 108 L111 88 L116 91 L100 118 L84 91 Z" fill="rgba(255,255,255,.2)" />

            {/* neck + head */}
            <rect x="92" y="66" width="16" height="24" rx="5" fill={skin} />
            <path d="M92 74 C98 84 102 84 108 74 L108 82 C102 90 98 90 92 82 Z" fill={skinShade} />
            <ellipse cx="100" cy="48" rx="17" ry="21" fill={skin} />
            <ellipse cx="100" cy="48" rx="17" ry="21" fill={`url(#${id}-skin)`} />
            {hairStyle === "short" && <path d="M82 46 C80 22 120 22 118 46 C112 36 94 34 82 46 Z" fill={hair} />}
            {hairStyle === "long" && <path d="M82 46 C82 24 118 24 118 46 C112 36 92 34 82 46 Z" fill={hair} />}
            {hairStyle === "bun" && (
                <>
                    <circle cx="100" cy="22" r="9" fill={hair} />
                    <path d="M82 46 C80 22 120 22 118 46 C112 34 94 32 82 46 Z" fill={hair} />
                </>
            )}
        </g></g>
    );
};

const Grain = () => (
    <>
        <filter id="hp2-grain"><feTurbulence type="fractalNoise" baseFrequency=".9" numOctaves="2" stitchTiles="stitch" /><feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 .55 0" /></filter>
        <rect width="400" height="500" filter="url(#hp2-grain)" opacity=".12" />
    </>
);

const SCENES = [
    {
        key: "person", label: "On a person",
        draw: () => (
            <>
                <defs>
                    <linearGradient id="hp2-bg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#e6dfd3" /><stop offset="1" stopColor="#bdb2a1" /></linearGradient>
                    <linearGradient id="hp2-beam1" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fff" stopOpacity=".55" /><stop offset="1" stopColor="#fff" stopOpacity="0" /></linearGradient>
                </defs>
                <rect width="400" height="500" fill="url(#hp2-bg1)" />
                <path d="M0 0 L230 0 L120 500 L0 500 Z" fill="url(#hp2-beam1)" />
                <rect y="466" width="400" height="34" fill="rgba(0,0,0,.12)" />
                <Figure id="f1" skin="#c48a62" hair="#241812" hairStyle="long" bottom="#2b3550" bottomType="trouser" transform="translate(85 62) scale(1.15)" />
            </>
        ),
    },
    {
        key: "another", label: "Another person",
        draw: () => (
            <>
                <defs>
                    <linearGradient id="hp2-bg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#d6dde4" /><stop offset="1" stopColor="#9eacb9" /></linearGradient>
                </defs>
                <rect width="400" height="500" fill="url(#hp2-bg2)" />
                <circle cx="300" cy="150" r="130" fill="rgba(255,255,255,.28)" />
                <rect y="466" width="400" height="34" fill="rgba(0,0,0,.14)" />
                <Figure id="f2" skin="#86563a" hair="#14100e" hairStyle="short" bottom="#3a3d42" bottomType="trouser" transform="translate(85 62) scale(1.15)" />
            </>
        ),
    },
    {
        key: "backdrop", label: "New backdrop",
        draw: () => (
            <>
                <defs>
                    <linearGradient id="hp2-bg3" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#f0cfa8" /><stop offset="1" stopColor="#c8845a" /></linearGradient>
                </defs>
                <rect width="400" height="500" fill="url(#hp2-bg3)" />
                <circle cx="200" cy="170" r="104" fill="#f7e3c4" opacity=".7" />
                <path d="M70 500 V230 C70 150 330 150 330 230 V500 Z" fill="rgba(110,50,25,.28)" />
                <rect y="466" width="400" height="34" fill="rgba(80,30,10,.22)" />
                <Figure id="f3" skin="#e2b48f" hair="#5b3921" hairStyle="bun" bottom="#efe8dc" bottomType="skirt" shoe="#5b3921" transform="translate(85 62) scale(1.15)" />
            </>
        ),
    },
    {
        key: "clip", label: "Short clip", clip: true,
        draw: () => (
            <>
                <defs>
                    <linearGradient id="hp2-bg4" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#b9cbbd" /><stop offset="1" stopColor="#6c8574" /></linearGradient>
                </defs>
                <rect width="400" height="500" fill="url(#hp2-bg4)" />
                <g className="hp2-drift">
                    <circle cx="70" cy="120" r="46" fill="rgba(255,255,255,.22)" />
                    <circle cx="330" cy="210" r="64" fill="rgba(255,255,255,.16)" />
                    <circle cx="260" cy="70" r="26" fill="rgba(255,255,255,.2)" />
                </g>
                <rect y="466" width="400" height="34" fill="rgba(0,0,0,.16)" />
                <Figure id="f4" skin="#c48a62" hair="#241812" hairStyle="long" bottom="#23303f" bottomType="trouser" sway transform="translate(85 62) scale(1.15)" />
            </>
        ),
    },
];

const Scene = ({ scene, index }) => (
    <div className="hp2-scene absolute inset-0" style={{ animationDelay: `${index * SCENE_SECONDS}s` }}>
        {scene.src ? (
            <img src={scene.src} alt={scene.label} className="absolute inset-0 h-full w-full object-cover" />
        ) : scene.video ? (
            <video src={scene.video} className="absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline />
        ) : (
            <svg viewBox="0 0 400 500" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full" aria-hidden="true">
                {scene.draw()}
                <Grain />
            </svg>
        )}
        <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-black/45 px-3 py-1.5 text-xs font-medium text-white backdrop-blur">
            {scene.clip && <span className="hp2-rec h-2 w-2 rounded-full bg-[#ff5b45]" />}
            {scene.label}
        </div>
        {scene.clip && (
            <div className="absolute inset-x-4 bottom-4 flex items-center gap-3 rounded-full bg-black/45 px-4 py-2 text-[11px] text-white backdrop-blur">
                <svg viewBox="0 0 10 10" className="h-3 w-3 shrink-0" fill="#fff" aria-hidden="true"><path d="M2 1 L9 5 L2 9 Z" /></svg>
                <span className="h-1 flex-1 overflow-hidden rounded bg-white/30"><span className="hp2-scrub block h-full bg-white" /></span>
                <span className="tabular-nums">0:08</span>
            </div>
        )}
    </div>
);

const HeroProof = ({ scenes = SCENES }) => (
    <div className="hero-proof-v2 relative mx-auto w-full max-w-md" role="img" aria-label="One olive shirt shown on a person, on another person, on a new backdrop, and as a short clip.">
        <style>{`
            @keyframes hp2Scene { 0% {opacity:0; transform:scale(1.04)} 6%,22% {opacity:1; transform:scale(1)} 28%,100% {opacity:0; transform:scale(1)} }
            @keyframes hp2Dot { 0%,2% {background:var(--hp2-dot-off); width:6px} 6%,22% {background:#be3120; width:22px} 28%,100% {background:var(--hp2-dot-off); width:6px} }
            @keyframes hp2Ring { 0% {box-shadow:0 0 0 0 rgba(190,49,32,.55)} 14%,100% {box-shadow:0 0 0 14px rgba(190,49,32,0)} }
            @keyframes hp2Sway { 0%,100% {transform:rotate(-1.4deg)} 50% {transform:rotate(1.4deg)} }
            @keyframes hp2Drift { 0%,100% {transform:translateX(-10px)} 50% {transform:translateX(14px)} }
            @keyframes hp2Scrub { 0% {width:0} 100% {width:100%} }
            @keyframes hp2Rec { 0%,100% {opacity:1} 50% {opacity:.25} }
            .hero-proof-v2 { --hp2-dot-off: rgba(128,128,128,.4); }
            .hp2-scene { opacity:0; animation: hp2Scene ${SCENE_SECONDS * 4}s ease-in-out infinite backwards; }
            .hp2-dot { animation: hp2Dot ${SCENE_SECONDS * 4}s ease-in-out infinite; background: var(--hp2-dot-off); width:6px; }
            .hp2-ring { animation: hp2Ring ${SCENE_SECONDS}s ease-out infinite; }
            .hp2-sway { transform-origin: 100px 346px; transform-box: view-box; animation: hp2Sway 3s ease-in-out infinite; }
            .hp2-drift { animation: hp2Drift 4s ease-in-out infinite; }
            .hp2-scrub { width:0; animation: hp2Scrub ${SCENE_SECONDS}s linear infinite; }
            .hp2-rec { animation: hp2Rec 1.2s ease-in-out infinite; }
            @media (prefers-reduced-motion: reduce) {
                .hp2-scene { animation:none; opacity:0; }
                .hp2-scene:first-child { opacity:1; }
                .hp2-dot, .hp2-ring, .hp2-sway, .hp2-drift, .hp2-scrub, .hp2-rec { animation:none; }
                .hp2-scrub { width:60%; }
            }
        `}</style>

        <div className="relative aspect-[4/5] overflow-hidden rounded-[28px] border border-hairline shadow-[0_30px_80px_-30px_rgba(0,0,0,.7)]">
            {scenes.map((s, i) => <Scene key={s.key} scene={s} index={i} />)}
        </div>

        <div className="hp2-ring absolute -bottom-6 -left-6 w-[34%] rounded-2xl border border-hairline bg-sheet p-3 shadow-[0_18px_40px_-16px_rgba(0,0,0,.65)]">
            <div className="flex aspect-square items-center justify-center rounded-xl bg-tray">
                <svg viewBox="0 0 120 120" className="w-4/5" aria-hidden="true">
                    <defs>
                        <linearGradient id="hp2-flat" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fff" stopOpacity=".2" /><stop offset="1" stopColor="#000" stopOpacity=".28" /></linearGradient>
                    </defs>
                    <path d="M38 16 L18 28 L8 56 L26 62 L32 48 L32 104 L88 104 L88 48 L94 62 L112 56 L102 28 L82 16 C76 26 44 26 38 16 Z" fill={SHIRT} />
                    <path d="M38 16 L18 28 L8 56 L26 62 L32 48 L32 104 L88 104 L88 48 L94 62 L112 56 L102 28 L82 16 C76 26 44 26 38 16 Z" fill="url(#hp2-flat)" />
                    <path d="M60 26 L60 104" stroke="rgba(0,0,0,.18)" strokeWidth="1.4" />
                </svg>
            </div>
            <span className="mt-2 block text-center text-[11px] font-medium text-muted">Your garment</span>
        </div>

        <div className="mt-6 flex items-center justify-center gap-1.5 pl-[30%]" aria-hidden="true">
            {scenes.map((s, i) => <span key={s.key} className="hp2-dot h-1.5 rounded-full" style={{ animationDelay: `${i * SCENE_SECONDS}s` }} />)}
        </div>
    </div>
);

export default HeroProof;
